import * as React from "react";
import useModernLayoutEffect from "use-isomorphic-layout-effect";

export const SafeReact = {
  ...React,
};

export function contains(parent, child) {
  if (!parent || !child) {
    return false;
  }

  const rootNode = child.getRootNode?.();

  // First, attempt with faster native method
  if (parent.contains(child)) {
    return true;
  }

  // then fallback to custom implementation with Shadow DOM support
  if (rootNode && isShadowRoot(rootNode)) {
    let next = child;
    while (next) {
      if (parent === next) {
        return true;
      }
      // @ts-ignore
      next = next.parentNode || next.host;
    }
  }

  // Give up, the result is false
  return false;
}

export function getDocument(node) {
  return node?.ownerDocument || document;
}

export function isMouseLikePointerType(pointerType, strict) {
  // On some Linux machines with Chromium, mouse inputs return a `pointerType`
  // of "pen": https://github.com/floating-ui/floating-ui/issues/2015
  const values = ["mouse", "pen"];
  if (!strict) {
    values.push("", undefined);
  }
  return values.includes(pointerType);
}

export function createAttribute(name) {
  return `data-floating-ui-${name}`;
}

export function clearTimeoutIfSet(timeoutRef) {
  if (timeoutRef.current !== -1) {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = -1;
  }
}

export function useLatestRef(value) {
  const ref = React.useRef(value);
  useModernLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

const useInsertionEffect = SafeReact.useInsertionEffect;

const useSafeInsertionEffect = useInsertionEffect || ((fn) => fn());

export function useEffectEvent(callback) {
  const ref = React.useRef(() => {
    if (__DEV__) {
      throw new Error("Cannot call an event handler while rendering.");
    }
  });

  useSafeInsertionEffect(() => {
    ref.current = callback;
  });

  return React.useCallback((...args) => ref.current?.(...args), []);
}
